import * as Cesium from 'cesium';
import { LayerAction } from '../../enums/mapPortalActions';
import { MapRenderError } from '../../enums/mapRenderError';
import { ApiRoutes } from '../../routes/routeConstants/apiRoutes';

export const MAP_ARIA_LABEL = 'Infrastructure map';

export const MAP_RENDER_ERROR_MESSAGES: Record<MapRenderError, string> = {
  [MapRenderError.UNSUPPORTED]: "The map can't be shown because this browser or device doesn't support WebGL.",
  [MapRenderError.RENDER_FAILED]: 'The map stopped rendering.',
};

export const LAYER_PROCESSING_REFETCH_MS = 60_000;
// PARITY: mirrors the toolbox colour default in src/views/MapPortal/ExpandedMapView/index.tsx — re-check when that flow changes
export const DEFAULT_TOOL_COLOR = '#1677ff';
// Same endpoints as Map Portal, cached apart from it: legend edits here never touch Map Portal's cache.
export const COMMON_MAP_QUERY_SCOPE = 'commonMap';
export const commonMapQueryKey = (queryKey: readonly unknown[]) => [COMMON_MAP_QUERY_SCOPE, ...queryKey];
// PARITY: mirrors the visibility save debounce in src/views/MapPortal/Legend/LegendDrawer.tsx — re-check when that flow changes
export const LEGEND_VISIBILITY_SAVE_DELAY_MS = 500;

export const WMS_URL = `${ApiRoutes.GEOSERVER_BASE_URL}${import.meta.env.VITE_APP_GEOSERVER_WMS_PATH || '/geoserver/mapaki/wms'}`;
export const GEOSERVER_WORKSPACE = 'mapaki';
export const WMS_TILE_SIZE = 256;

export const DEFAULT_SCENE_MODE = Cesium.SceneMode.SCENE2D;
// Map Portal falls back to this height when the saved position has no zoom.
export const DEFAULT_CAMERA_HEIGHT = 25000000;

// Scale-range defaults, in camera height (m), matching Map Portal.
export const SCALE_RANGE_MAX_HEIGHT = 200000000;
export const SCALE_RANGE_MIN_HEIGHT = 100;
// Fraction of camera movement that re-evaluates layer scale ranges mid-gesture.
export const CAMERA_CHANGE_THRESHOLD = 0.1;

// Client labels: ~1 per 2 000 CSS px² is the readability limit, and bounds the per-layer cost.
export const LABEL_PX_PER_FEATURE = 2000;
export const LABEL_MIN_CAP = 200;
export const LABEL_MAX_CAP = 2000;
export const LABEL_FETCH_CONCURRENCY = 4;
// Labels refresh this long after the last camera change; Cesium's own moveEnd waits 500 ms.
export const CAMERA_SETTLE_MS = 150;
// Fetched area is padded so small pans reuse it. Zooming in past the ratio refetches; a capped
// response refetches sooner, since zooming in reveals labels it had to leave out.
export const VIEW_PADDING_RATIO = 0.25;
// Margin around a module's fitted extent: a share of its size, and at least this many degrees (about 1 km) so a point still gets a view.
export const EXTENT_PADDING_RATIO = 0.1;
export const EXTENT_MIN_PADDING = 0.01;
export const VIEW_REFRESH_ZOOM_RATIO = 0.5;
export const CAPPED_VIEW_REFRESH_ZOOM_RATIO = 0.8;
// PARITY: mirrors the label render fallbacks in src/hooks/useCesiumLayers.ts (used only when a saved value is empty) — re-check when that flow changes
export const DEFAULT_LABEL_COLOR = '#FFFFFF';
export const DEFAULT_HALO_COLOR = '#000000';
export const DEFAULT_LABEL_FONT_SIZE = 12;
export const DEFAULT_LABEL_FONT_FAMILY = 'sans-serif';

// Clusters and effects: ~1 feature per 200 CSS px² is the readability limit.
export const FEATURE_PX_PER_FEATURE = 200;
export const FEATURE_MIN_CAP = 5000;
export const FEATURE_MAX_CAP = 20000;
export const FEATURE_FETCH_CONCURRENCY = 4;

// PARITY: mirrors the cluster and effect render fallbacks in src/hooks/useCesiumLayers.ts — re-check when that flow changes
export const DEFAULT_CLUSTER_RADIUS = 40;
export const DEFAULT_CLUSTER_COLOR = '#F3D34B';
export const DEFAULT_POINT_FILL = '#fff';
export const DEFAULT_ICON_SIZE = 24;
export const POINT_PIXEL_SIZE = 10;
export const CLUSTER_IMAGE_SIZE = 128;
export const CLUSTER_IMAGE_RADIUS = 32;
// Hover picks re-render the scene, so spiral-on-hover picks at most this often.
export const SPIRAL_HOVER_THROTTLE_MS = 50;
// "Disable at" zoom level → camera height (m); clustering turns off below it.
export const CLUSTER_DISABLE_HEIGHTS: Record<number, number> = {
  4: 20000000,
  6: 10000000,
  8: 5000000,
  10: 1200000,
  12: 400000,
  14: 100000,
  16: 20000,
  18: 8000,
};
export const DEFAULT_CLUSTER_DISABLE_HEIGHT = 400000;
export const DEFAULT_GLOW_COLOR = '#00FFFF';
export const DEFAULT_GLOW_STRENGTH = 5;
export const DEFAULT_GLOW_RADIUS = 10;
export const GREYSCALE_LINE_WIDTH = 2;

// PARITY: mirrors the option order in src/views/MapPortal/Legend/LayerOption.tsx — re-check when that flow changes
export const LAYER_MENU_ACTIONS = [
  LayerAction.ZOOM_TO_LAYER,
  LayerAction.SHOW_PROPERTIES,
  LayerAction.SHOW_TABLE,
  LayerAction.DOWNLOAD_LAYER,
  LayerAction.DUPLICATE_LAYER,
  LayerAction.SAVE_LAYER_AS,
  LayerAction.REMOVE_LAYER,
  LayerAction.CREATE_GROUP,
  LayerAction.LAYER_PERMISSIONS,
  LayerAction.RE_UPLOAD_DATA,
];
// Options that open a flow in MapFlows.
export const LAYER_FLOW_ACTIONS = new Set([
  LayerAction.SHOW_PROPERTIES,
  LayerAction.RE_UPLOAD_DATA,
  LayerAction.SHOW_TABLE,
  LayerAction.DOWNLOAD_LAYER,
  LayerAction.DUPLICATE_LAYER,
  LayerAction.SAVE_LAYER_AS,
  LayerAction.REMOVE_LAYER,
  LayerAction.CREATE_GROUP,
  LayerAction.LAYER_PERMISSIONS,
]);
// PARITY: mirrors the Remove Layer and group-name copy in src/views/MapPortal/Legend/LayerOption.tsx and LegendDrawer.tsx — re-check when that flow changes
export const REMOVE_LAYER_TITLE = 'Remove Layer from Map?';
export const REMOVE_LAYER_MESSAGE = 'This will not remove the layer from the file manager.';
export const DUPLICATE_GROUP_NAME_ERROR = 'A group with this name already exists';
// Options Map Portal shows for every layer type; the rest are vector-only.
export const ALL_LAYER_TYPE_ACTIONS = new Set([LayerAction.ZOOM_TO_LAYER, LayerAction.SHOW_PROPERTIES, LayerAction.REMOVE_LAYER]);
// PARITY: mirrors the zoom-to-layer flight in src/hooks/useCesiumCamera.ts — re-check when that flow changes
export const ZOOM_TO_LAYER_SECONDS = 1.5;
export const SELECTION_FLY_SECONDS = 1;
// Targets within this many view widths are panned to; farther ones arc out and back in, which crosses fewer tiles.
export const FLY_PAN_MAX_VIEWS = 3;
// Cesium locks camera input for a whole flight, so any of these cancels it instead.
export const FLIGHT_CANCEL_INPUTS = [
  Cesium.ScreenSpaceEventType.LEFT_DOWN,
  Cesium.ScreenSpaceEventType.RIGHT_DOWN,
  Cesium.ScreenSpaceEventType.MIDDLE_DOWN,
  Cesium.ScreenSpaceEventType.WHEEL,
  Cesium.ScreenSpaceEventType.PINCH_START,
];
export const NO_FEATURES_TO_ZOOM_MESSAGE = 'No features to zoom to in this layer.';

// PARITY: mirrors the search timings in src/views/MapPortal/MapPortalMap/MapSearch.tsx — re-check when that flow changes
export const SEARCH_DEBOUNCE_MS = 400;
export const SEARCH_FLY_SECONDS = 2;
// Camera height (m) when flying to a point result.
export const SEARCH_PIN_HEIGHT = 3000;

export const HEATMAP_MAX_DIMENSION = 2048;
export const HEATMAP_PADDING_RATIO = 0.15;
export const HEATMAP_MIN_PADDING = 0.1;

// Excluded from the render signature: ordering and visibility are applied without a rebuild.
export const RENDER_SIGNATURE_EXCLUDED_KEYS = new Set(['sort_order', 'sortOrder', 'order', 'toggled', 'displayOnMap']);

export const VIEWER_OPTIONS: Cesium.Viewer.ConstructorOptions = {
  animation: false,
  timeline: false,
  fullscreenButton: false,
  infoBox: false,
  selectionIndicator: false,
  homeButton: false,
  geocoder: false,
  navigationHelpButton: false,
  baseLayerPicker: true,
  sceneModePicker: true,
  // Rendering failures are shown by CommonMap's own overlay, not Cesium's raw panel.
  showRenderLoopErrors: false,
  shouldAnimate: false,
  requestRenderMode: true,
  maximumRenderTimeChange: Infinity,
  msaaSamples: 1,
  skyBox: false,
  skyAtmosphere: false,
};
